import { useState, useRef } from 'react'
import { flushSync } from 'react-dom'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import CustomMessageService from 'services/CustomMessage'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import customMessageValidator from 'validators/customMessageValidator'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  Input,
  TextArea,
  Row,
  InfoText,
  Label,
  WarningText,
  ErrorContainer,
  VariableRow,
  VariableButton,
  HintText
} from './styles'

export default function CustomMessageModal ({
  visible,
  data: customMessage,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const contentRef = useRef(null)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{customMessage ? 'Editar mensagem personalizada' : 'Nova mensagem personalizada'}</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={customMessageValidator}
          initialValues={{
            'title': customMessage ? customMessage.title : '',
            'content': customMessage ? customMessage.content : ''
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (customMessage) {
                const { data: updatedMessage } = await cutileiApi.put (
                  `/custom_messages/${customMessage.id}`, values, requestConfig
                )
                handleConfirm (updatedMessage)
              } else {
                const { data: createdMessage } = await cutileiApi.post (
                  `/businesses/${businessId}/custom_messages`, values, requestConfig
                )
                handleConfirm (createdMessage)
              }

              setSubmitting (false)
              handleClose ()
            } catch (error) {
              setErrorMessage (getApiErrorMessage (error))
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue
          }) => {
            /*
              A variável entra no cursor, trocando o trecho selecionado, ou no fim se o texto não
              está em foco: um textarea que ninguém tocou diz que o cursor está no início. O
              `flushSync` escreve o texto novo antes de o cursor voltar para depois da variável,
              senão o React o jogaria para o fim ao atualizar o valor.
            */
            const insertVariable = variable => {
              const textArea = contentRef.current
              const content = textArea.value
              const hasFocus = document.activeElement === textArea
              const start = hasFocus ? textArea.selectionStart : content.length
              const end = hasFocus ? textArea.selectionEnd : content.length
              const caret = start + variable.token.length

              flushSync (() => {
                setFieldValue ('content', content.slice (0, start) + variable.token + content.slice (end))
              })

              textArea.focus ()
              textArea.setSelectionRange (caret, caret)
            }

            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                    <Row>
                      <FormField>
                        <Input
                          placeholder='Título'
                          value={values.title}
                          onChange={handleChange ('title')}
                          onBlur={handleBlur ('title')}
                        />
                        {touched.title && errors.title && (
                          <WarningText>
                            {errors.title}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Row>
                      <FormField>
                        <TextArea
                          ref={contentRef}
                          placeholder='Mensagem'
                          rows={5}
                          value={values.content}
                          onChange={handleChange ('content')}
                          onBlur={handleBlur ('content')}
                        />
                        {touched.content && errors.content && (
                          <WarningText>
                            {errors.content}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Label>Inserir no texto:</Label>
                    <VariableRow role='group' aria-label='Inserir no texto'>
                      {CustomMessageService.VARIABLES.map(variable => (
                        <VariableButton
                          key={variable.key}
                          type='button'
                          title={variable.token}
                          // Sem isto o clique tira o foco do texto, e com ele a posição do cursor
                          onMouseDown={event => event.preventDefault()}
                          onClick={() => insertVariable(variable)}
                        >
                          {variable.label}
                        </VariableButton>
                      ))}
                    </VariableRow>
                    <HintText>
                      Ao enviar, cada variável é trocada pelo dado do agendamento.
                    </HintText>
                    {errorMessage && (
                      <ErrorContainer>
                        <InfoText>{errorMessage}</InfoText>
                      </ErrorContainer>
                    )}
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label={customMessage ? 'Salvar alterações' : 'Criar mensagem'}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={180}
                  />
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
